import { useState, useMemo, useEffect } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { Check, ChevronLeft, ChevronRight, Loader2, PartyPopper } from "lucide-react";
import { Seo } from "@/lib/seo";
import { api, formatApiError } from "@/lib/api";
import {
  EVENT_TYPES, PRESTATIONS, FORMATS, CUISINES, COORDINATION_TYPES, CHEF_OCCASIONS,
} from "@/lib/data";

const track = (event, extra = {}) => {
  try { window.dispatchEvent(new CustomEvent("ar_track", { detail: { event, ...extra } })); } catch (e) {}
};

function RadioGroup({ name, options, value, onChange, testPrefix }) {
  return (
    <div className="grid gap-3 sm:grid-cols-2">
      {options.map((opt) => (
        <button
          type="button"
          key={opt}
          data-testid={`${testPrefix}-${opt}`}
          onClick={() => onChange(opt)}
          className={`flex items-center gap-3 rounded-xl border px-4 py-3 text-left text-sm transition-colors duration-200 ${
            value === opt ? "border-[#C9A84C] bg-[#C9A84C]/15 text-[#2C2C2C]" : "border-[#2C2C2C]/15 bg-white text-[#2C2C2C]/80 hover:border-[#C9A84C]/60"
          }`}
        >
          <span className={`flex h-4 w-4 shrink-0 items-center justify-center rounded-full border ${value === opt ? "border-[#C9A84C] bg-[#C9A84C]" : "border-[#2C2C2C]/30"}`}>
            {value === opt && <Check size={11} className="text-white" />}
          </span>
          {opt}
        </button>
      ))}
    </div>
  );
}

const Field = ({ label, required, children, hint }) => (
  <div>
    <label className="mb-1.5 block text-sm font-medium text-[#2C2C2C]">
      {label} {required && <span className="text-[#C9A84C]">*</span>}
    </label>
    {children}
    {hint && <p className="mt-1 text-xs text-[#2C2C2C]/50">{hint}</p>}
  </div>
);

const inputCls = "w-full rounded-lg border border-[#2C2C2C]/20 bg-white px-4 py-2.5 text-sm text-[#2C2C2C] outline-none transition-colors focus:border-[#C9A84C] focus:ring-1 focus:ring-[#C9A84C]";

export default function Devis() {
  const [params] = useSearchParams();
  const initService = params.get("service") || "";
  const initType = params.get("type") || (EVENT_TYPES.includes(initService) ? initService : initService === "Cheffe à domicile" ? "Événement privé" : "");
  const [step, setStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);
  const [errors, setErrors] = useState({});
  const [form, setForm] = useState({
    event_type: EVENT_TYPES.includes(initType) ? initType : "", prestation: PRESTATIONS.includes(initService) ? initService : "", event_date: "", event_location: "",
    guests: "", budget: "", format_traiteur: "", cuisine: "", coordination_type: "",
    chef_occasion: "", dietary: "", first_name: "", last_name: "", phone: "", email: "",
    message: "", consent: false, website: "",
  });

  useEffect(() => { track("form_start"); }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const isChef = form.prestation === "Cheffe à domicile";
  const showTraiteur = ["Traiteur uniquement", "Traiteur et coordination", "Je souhaite être conseillée"].includes(form.prestation);
  const showCoordination = ["Coordination événementielle uniquement", "Traiteur et coordination", "Je souhaite être conseillée"].includes(form.prestation);

  const validateStep1 = () => {
    const e = {};
    if (!form.event_type) e.event_type = "Veuillez choisir un type d'événement.";
    if (!form.prestation) e.prestation = "Veuillez choisir une prestation.";
    if (!form.event_date) e.event_date = "La date est obligatoire.";
    if (!form.event_location.trim()) e.event_location = "Le lieu est obligatoire.";
    setErrors(e);
    return Object.keys(e).length === 0;
  };
  const validateStep2 = () => {
    const e = {};
    if (!form.guests.trim()) e.guests = "Le nombre de personnes est obligatoire.";
    if (!form.budget.trim()) e.budget = "Le budget envisagé est obligatoire.";
    setErrors(e);
    return Object.keys(e).length === 0;
  };
  const validateStep3 = () => {
    const e = {};
    if (!form.first_name.trim()) e.first_name = "Prénom obligatoire.";
    if (!form.last_name.trim()) e.last_name = "Nom obligatoire.";
    if (!/^[+\d][\d\s().-]{6,}$/.test(form.phone.trim())) e.phone = "Numéro de téléphone invalide.";
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.email.trim())) e.email = "Adresse e-mail invalide.";
    if (!form.consent) e.consent = "Le consentement est requis.";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const next = () => {
    const ok = step === 1 ? validateStep1() : step === 2 ? validateStep2() : true;
    if (ok) { track(`form_step_${step}_complete`); setStep((s) => s + 1); window.scrollTo({ top: 0, behavior: "smooth" }); }
  };
  const back = () => { setErrors({}); setStep((s) => s - 1); };

  const submit = async () => {
    if (!validateStep3()) return;
    track("form_submit_attempt");
    setSubmitting(true);
    try {
      const res = await api.post("/quotes", form);
      setDone(res.data.reference);
      track("form_submit_success", { reference: res.data.reference });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Échec de l'envoi. Veuillez réessayer.");
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div className="min-h-screen bg-[#FAF6EF] pt-32" data-testid="devis-success">
        <div className="mx-auto max-w-xl px-5 py-20 text-center">
          <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-[#C9A84C]/20 text-[#C9A84C]">
            <PartyPopper size={30} />
          </div>
          <h1 className="mt-6 font-serif text-3xl font-500 text-[#2C2C2C]">Votre demande est bien envoyée</h1>
          <p className="mt-4 text-[#2C2C2C]/70">
            Merci ! Votre demande de devis a bien été enregistrée. Notre équipe vous recontactera très prochainement.
          </p>
          <p className="mt-4 inline-block rounded-lg border border-[#2C2C2C]/10 bg-white px-5 py-3 text-sm text-[#2C2C2C]">
            Référence de votre demande : <strong data-testid="quote-reference">{done}</strong>
          </p>
          <div className="mt-8">
            <Link to="/" className="inline-flex items-center gap-2 rounded-full bg-[#2C2C2C] px-7 py-3 text-sm text-white transition-colors hover:bg-[#C9A84C] hover:text-[#2C2C2C]" data-testid="success-home-btn">
              Retour à l'accueil
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAF6EF] pt-28" data-testid="devis-page">
      <Seo title="Demander un devis — Afro Regal" description="Présentez-nous votre événement et recevez une proposition adaptée. Devis gratuit et sans engagement." />
      <div className="mx-auto max-w-2xl px-5 py-14 lg:px-8">
        <div className="mb-10 text-center">
          <p className="mb-3 text-sm uppercase tracking-[0.25em] text-[#C9A84C]" data-testid="devis-eyebrow">{isChef ? "Devis cheffe à domicile" : "Demande de devis"}</p>
          <h1 className="font-serif text-3xl font-500 text-[#2C2C2C] lg:text-4xl">{isChef ? "Organisons votre dîner privé" : "Parlons de votre événement"}</h1>
        </div>

        {/* Progress */}
        <div className="mb-10 flex items-center justify-center gap-2" data-testid="form-progress">
          {[1, 2, 3].map((s) => (
            <div key={s} className="flex items-center gap-2">
              <div className={`flex h-8 w-8 items-center justify-center rounded-full text-sm font-medium transition-colors ${step >= s ? "bg-[#2C2C2C] text-white" : "bg-[#2C2C2C]/10 text-[#2C2C2C]/50"}`}>{s}</div>
              {s < 3 && <div className={`h-px w-10 ${step > s ? "bg-[#2C2C2C]" : "bg-[#2C2C2C]/15"}`} />}
            </div>
          ))}
        </div>

        <div className="rounded-2xl border border-[#2C2C2C]/10 bg-[#FAF6EF] p-6 lg:p-8">
          {/* Honeypot */}
          <input type="text" tabIndex={-1} autoComplete="off" value={form.website} onChange={(e) => set("website", e.target.value)} className="hidden" aria-hidden="true" />

          {step === 1 && (
            <div className="space-y-7 fade-up" data-testid="form-step-1">
              <div>
                <Field label="Type d'événement" required />
                <RadioGroup testPrefix="event-type" options={EVENT_TYPES} value={form.event_type} onChange={(v) => set("event_type", v)} />
                {errors.event_type && <p className="mt-2 text-xs text-red-600" data-testid="err-event_type">{errors.event_type}</p>}
              </div>
              <div>
                <Field label="Prestation souhaitée" required />
                <RadioGroup testPrefix="prestation" options={PRESTATIONS} value={form.prestation} onChange={(v) => set("prestation", v)} />
                {errors.prestation && <p className="mt-2 text-xs text-red-600" data-testid="err-prestation">{errors.prestation}</p>}
              </div>
              <div className="grid gap-5 sm:grid-cols-2">
                <Field label="Date de l'événement" required>
                  <input type="date" data-testid="input-event-date" value={form.event_date} onChange={(e) => set("event_date", e.target.value)} className={inputCls} />
                  {errors.event_date && <p className="mt-1 text-xs text-red-600">{errors.event_date}</p>}
                </Field>
                <Field label="Ville ou lieu" required>
                  <input type="text" data-testid="input-event-location" placeholder="Ex. Compiègne" value={form.event_location} onChange={(e) => set("event_location", e.target.value)} className={inputCls} />
                  {errors.event_location && <p className="mt-1 text-xs text-red-600">{errors.event_location}</p>}
                </Field>
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-7 fade-up" data-testid="form-step-2">
              <div className="grid gap-5 sm:grid-cols-2">
                <Field label="Nombre de personnes" required hint="Saisissez votre nombre de convives">
                  <input type="text" data-testid="input-guests" placeholder="Ex. 80" value={form.guests} onChange={(e) => set("guests", e.target.value)} className={inputCls} />
                  {errors.guests && <p className="mt-1 text-xs text-red-600">{errors.guests}</p>}
                </Field>
                <Field label="Budget envisagé" required hint="Montant libre ou « À définir avec Afro Regal »">
                  <input type="text" data-testid="input-budget" placeholder="À définir avec Afro Regal" value={form.budget} onChange={(e) => set("budget", e.target.value)} className={inputCls} />
                  {errors.budget && <p className="mt-1 text-xs text-red-600">{errors.budget}</p>}
                </Field>
              </div>

              {showTraiteur && !isChef && (
                <div className="space-y-6 rounded-xl border border-[#C9A84C]/30 bg-white p-5" data-testid="traiteur-questions">
                  <p className="text-xs font-medium uppercase tracking-wide text-[#C9A84C]">Questions traiteur</p>
                  <div>
                    <Field label="Format souhaité" />
                    <RadioGroup testPrefix="format" options={FORMATS} value={form.format_traiteur} onChange={(v) => set("format_traiteur", v)} />
                  </div>
                  <div>
                    <Field label="Cuisine souhaitée" />
                    <RadioGroup testPrefix="cuisine" options={CUISINES} value={form.cuisine} onChange={(v) => set("cuisine", v)} />
                  </div>
                </div>
              )}

              {showCoordination && !isChef && (
                <div className="space-y-4 rounded-xl border border-[#C9A84C]/30 bg-white p-5" data-testid="coordination-questions">
                  <p className="text-xs font-medium uppercase tracking-wide text-[#C9A84C]">Questions coordination</p>
                  <Field label="Type de coordination" />
                  <RadioGroup testPrefix="coordination" options={COORDINATION_TYPES} value={form.coordination_type} onChange={(v) => set("coordination_type", v)} />
                </div>
              )}

              {isChef && (
                <div className="space-y-6 rounded-xl border border-[#C9A84C]/30 bg-white p-5" data-testid="chef-questions">
                  <p className="text-xs font-medium uppercase tracking-wide text-[#C9A84C]">Cheffe à domicile</p>
                  <div>
                    <Field label="Type d'occasion" />
                    <RadioGroup testPrefix="chef-occasion" options={CHEF_OCCASIONS} value={form.chef_occasion} onChange={(v) => set("chef_occasion", v)} />
                  </div>
                  <Field label="Préférences culinaires ou contraintes alimentaires">
                    <textarea data-testid="input-dietary" rows={3} value={form.dietary} onChange={(e) => set("dietary", e.target.value)} className={inputCls} placeholder="Allergies, régimes, envies particulières…" />
                  </Field>
                </div>
              )}
            </div>
          )}

          {step === 3 && (
            <div className="space-y-5 fade-up" data-testid="form-step-3">
              <div className="grid gap-5 sm:grid-cols-2">
                <Field label="Prénom" required>
                  <input type="text" data-testid="input-first-name" value={form.first_name} onChange={(e) => set("first_name", e.target.value)} className={inputCls} />
                  {errors.first_name && <p className="mt-1 text-xs text-red-600">{errors.first_name}</p>}
                </Field>
                <Field label="Nom" required>
                  <input type="text" data-testid="input-last-name" value={form.last_name} onChange={(e) => set("last_name", e.target.value)} className={inputCls} />
                  {errors.last_name && <p className="mt-1 text-xs text-red-600">{errors.last_name}</p>}
                </Field>
              </div>
              <div className="grid gap-5 sm:grid-cols-2">
                <Field label="Téléphone" required>
                  <input type="tel" data-testid="input-phone" value={form.phone} onChange={(e) => set("phone", e.target.value)} className={inputCls} placeholder="06 00 00 00 00" />
                  {errors.phone && <p className="mt-1 text-xs text-red-600">{errors.phone}</p>}
                </Field>
                <Field label="Adresse e-mail" required>
                  <input type="email" data-testid="input-email" value={form.email} onChange={(e) => set("email", e.target.value)} className={inputCls} />
                  {errors.email && <p className="mt-1 text-xs text-red-600">{errors.email}</p>}
                </Field>
              </div>
              <Field label="Message ou précisions supplémentaires">
                <textarea data-testid="input-message" rows={4} value={form.message} onChange={(e) => set("message", e.target.value)} className={inputCls} />
              </Field>
              <label className="flex items-start gap-3 text-sm text-[#2C2C2C]/75">
                <input type="checkbox" data-testid="input-consent" checked={form.consent} onChange={(e) => set("consent", e.target.checked)} className="mt-0.5 h-4 w-4 accent-[#2C2C2C]" />
                <span>J'accepte que mes informations soient utilisées pour traiter ma demande de devis, conformément à la <Link to="/confidentialite" className="underline hover:text-[#C9A84C]">politique de confidentialité</Link>.</span>
              </label>
              {errors.consent && <p className="text-xs text-red-600" data-testid="err-consent">{errors.consent}</p>}
            </div>
          )}

          {/* Nav */}
          <div className="mt-9 flex items-center justify-between">
            {step > 1 ? (
              <button type="button" onClick={back} data-testid="form-back-btn" className="inline-flex items-center gap-1 text-sm text-[#2C2C2C]/70 hover:text-[#2C2C2C]">
                <ChevronLeft size={16} /> Précédent
              </button>
            ) : <span />}
            {step < 3 ? (
              <button type="button" onClick={next} data-testid="form-next-btn" className="inline-flex items-center gap-2 rounded-full bg-[#2C2C2C] px-7 py-3 text-sm text-white transition-colors hover:bg-[#C9A84C] hover:text-[#2C2C2C]">
                Continuer <ChevronRight size={16} />
              </button>
            ) : (
              <button type="button" onClick={submit} disabled={submitting} data-testid="form-submit-btn" className="inline-flex items-center gap-2 rounded-full bg-[#2C2C2C] px-7 py-3 text-sm text-white transition-colors hover:bg-[#C9A84C] hover:text-[#2C2C2C] disabled:opacity-60">
                {submitting && <Loader2 size={16} className="animate-spin" />} Envoyer ma demande de devis
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
